import React, { useState, useCallback } from "react";
import classNames from "classnames";
import type { CustomersSectionProps } from "./types";
import type { IServiceCustomer } from "@/services";
import { useCustomers } from "@/hooks/data";
import { Icon } from "@/components/ui/display/Icon";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { ConfirmDialog } from "@/components/ui/feedback/ConfirmDialog";
import { StatusMessages } from "@/components/ui/feedback/StatusMessages";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import { Button } from "@/components/ui/buttons/Button";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { TextInput } from "@/components/ui/forms/TextInput";
import { FilteredList } from "@/components/ui/lists/FilteredList";
import { CustomerListItem } from "@/components/ui/lists/CustomerListItem";
import layoutStyles from "@/components/admin/AdminSectionLayout.module.css";
import styles from "./CustomersSection.module.css";
import SectionHeader from "@/components/layout/SectionHeader";

/** Explanation text for customers section */
const CUSTOMERS_EXPLANATION =
  "Her kan du opprette nye kunder, søke opp eksisterende oppføringer og fjerne dem ved behov.";

/**
 * CustomersSection - View and add customers
 * Part of the Admin page
 *
 * Note: Edit and delete functionality would require additional API endpoints
 */
export const CustomersSection: React.FC<CustomersSectionProps> = ({
  className,
  "data-testid": testId = "customers-section",
}) => {
  const {
    sortedCustomers,
    isLoading,
    error,
    createCustomer,
    isCreating,
    deleteCustomer,
    isDeleting,
    findByName,
  } = useCustomers();

  // State for add form
  const [newCustomerName, setNewCustomerName] = useState("");

  // State for flash alerts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // State for mobile help tooltip
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<IServiceCustomer | null>(
    null,
  );

  // Handle add customer
  const handleAdd = useCallback(async () => {
    const trimmedName = newCustomerName.trim();
    if (!trimmedName) {
      setErrorMessage("Kundenavn kan ikke være tomt");
      return;
    }

    // Check if customer already exists
    if (findByName(trimmedName)) {
      setErrorMessage("En kunde med dette navnet eksisterer allerede");
      return;
    }

    const result = await createCustomer(trimmedName);

    if (result) {
      setSuccessMessage("Kunde opprettet");
      setNewCustomerName("");
    } else {
      setErrorMessage("Kunne ikke opprette kunde");
    }
  }, [newCustomerName, createCustomer, findByName]);

  const handleDeleteClick = useCallback((customer: IServiceCustomer) => {
    setDeleteTarget(customer);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;

    const success = await deleteCustomer(deleteTarget.id);
    if (success) {
      setSuccessMessage("Kunde slettet");
    } else {
      setErrorMessage("Kunne ikke slette kunde");
    }
    setDeleteTarget(null);
  }, [deleteCustomer, deleteTarget]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  // Handle form submit
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleAdd();
    },
    [handleAdd],
  );

  return (
    <div
      className={classNames(layoutStyles.section, styles.section, className)}
      data-testid={testId}
    >
      {/* Explanation text - visible only on desktop */}
      <p className={layoutStyles.explanationText}>{CUSTOMERS_EXPLANATION}</p>

      {/* Flash alerts */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
          data-testid={`${testId}-success-alert`}
        />
      )}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
          data-testid={`${testId}-error-alert`}
        />
      )}

      <div className={layoutStyles.dataAreaGrid}>
        <section
          className={classNames(layoutStyles.card, styles.formSection)}
          data-area="customer-form"
          data-testid={`${testId}-form-section`}
        >
          <SectionHeader
            as="h2"
            size="md"
            action={
              <div className={styles.helpButtonWrapper}>
                <Tooltip
                  content={CUSTOMERS_EXPLANATION}
                  position="left"
                  width="md"
                  show={showHelpTooltip}
                >
                  <IconButton
                    icon={<Icon name="help-circle" size="sm" aria-hidden />}
                    variant="default"
                    size="sm"
                    onClick={() => setShowHelpTooltip((prev) => !prev)}
                    aria-label="Hva kan jeg gjøre her?"
                    className={styles.helpButton}
                    data-testid={`${testId}-help-button`}
                  />
                </Tooltip>
              </div>
            }
          >
            Legg til kunde
          </SectionHeader>
          <form
            className={styles.addForm}
            onSubmit={handleSubmit}
            data-testid={`${testId}-add-form`}
          >
            <div className={styles.formField}>
              <label htmlFor="customer-name" className={styles.fieldLabel}>
                Kundenavn
              </label>
              <div className={styles.inputWrapper}>
                <TextInput
                  id="customer-name"
                  value={newCustomerName}
                  onChange={setNewCustomerName}
                  placeholder="Skriv inn kundens navn..."
                  disabled={isCreating}
                  fullWidth
                  data-testid={`${testId}-name-input`}
                />
              </div>
              <span className={styles.fieldHelp}>
                Opprett en ny kunde som kan brukes i bookinger
              </span>
            </div>

            <div className={styles.buttonRow}>
              <Button
                type="submit"
                disabled={!newCustomerName.trim()}
                isLoading={isCreating}
                loadingText="Oppretter..."
                className={styles.addButton}
                data-testid={`${testId}-add-button`}
              >
                Opprett kunde
              </Button>
            </div>
          </form>
        </section>

        <section
          className={classNames(layoutStyles.card, styles.listSection)}
          data-area="customer-list"
          data-testid={`${testId}-list-section`}
        >
          <SectionHeader as="h2" size="md">
            Kundeliste
          </SectionHeader>
          <StatusMessages
            error={error}
            isLoading={isLoading}
            errorLabel="Kunne ikke laste kunder"
            loadingLabel="Laster kunder..."
            testId={testId}
          />
          {!isLoading && !error && (
            <FilteredList
              items={sortedCustomers}
              filterItem={(customer: IServiceCustomer, normalized) =>
                customer.name.toLowerCase().includes(normalized)
              }
              renderItem={(customer: IServiceCustomer) => (
                <CustomerListItem
                  customer={customer}
                  onDelete={handleDeleteClick}
                  isDeleting={isDeleting}
                  data-testid={`${testId}-item-${customer.id}`}
                />
              )}
              placeholder="Søk etter kunde..."
              debounce={150}
              className={styles.listWrapper}
              maxHeight="clamp(200px, 40vh, 400px)"
              searchDisabled={isLoading}
              data-testid={`${testId}-filtered-list`}
            />
          )}
          <ConfirmDialog
            isOpen={deleteTarget !== null}
            title="Slett kunde"
            text="Å slette en kunde vil automatisk slette alle soft bookingene som er knyttet til kunden. Er du sikker?"
            confirmText="Ja"
            cancelText="Nei"
            destructive
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            data-testid={`${testId}-delete-dialog`}
          />
        </section>
      </div>
    </div>
  );
};

CustomersSection.displayName = "CustomersSection";

export default CustomersSection;
